import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Clock, CheckCircle2, ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { t } = useLanguage();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="relative overflow-hidden facet-soft">
      <div className="grain pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-2 lg:gap-12 lg:pb-24 lg:pt-20">
        {/* Left — no opacity fade: keeps text contrast deterministic for a11y scanners (axe sampled mid-fade) */}
        <motion.div
          initial={{ y: 12 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5, ease }}
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border-subtle)] bg-white/70 px-3 py-1 text-xs font-semibold tracking-wide text-[var(--brand-burgundy)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--brand-burgundy)]" />
            {t.hero.eyebrow}
          </span>
          <h1 className="mt-5 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-[var(--brand-navy)] sm:text-5xl lg:text-[3.4rem]">
            {t.hero.headline}
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[var(--muted-ink)] sm:text-lg">
            {t.hero.subheadline}
          </p>

          <ul className="mt-6 space-y-2.5">
            {t.hero.bullets.map((b) => (
              <li key={b} className="flex items-center gap-2.5 text-sm font-medium text-[var(--brand-charcoal)]">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-[var(--brand-burgundy)]" />
                {b}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button
              onClick={() => scrollTo("portal")}
              className="group h-12 rounded-xl bg-[var(--brand-burgundy)] px-6 text-sm font-semibold text-white shadow-[var(--shadow-md)] transition-colors hover:bg-[var(--brand-burgundy-dark)] active:scale-[0.98]"
              data-testid="hero-primary-cta"
            >
              {t.hero.primaryCta}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <Button
              onClick={() => scrollTo("services")}
              variant="outline"
              className="h-12 rounded-xl border-[color:var(--border-strong)] bg-white px-6 text-sm font-semibold text-[var(--brand-charcoal)] transition-colors hover:bg-[var(--surface-2)]"
              data-testid="hero-secondary-cta"
            >
              {t.hero.secondaryCta}
            </Button>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-[var(--muted-ink)]">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[var(--success)]" />{t.hero.badge}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-[var(--brand-burgundy)]" />{t.hero.bullets[2]}</span>
          </div>
        </motion.div>

        {/* Right */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.1 }}
          className="relative mx-auto w-full max-w-md"
        >
          <div className="absolute -inset-4 -z-10 rounded-[28px] bg-[image:var(--bg-facet)] opacity-70 blur-[2px]" />
          <div className="overflow-hidden rounded-[24px] border border-[color:var(--border-subtle)] bg-white shadow-[var(--shadow-float)]">
            <img
              src="/assets/angelina.jpg"
              alt="Ms. A Vidal, Founder"
              className="aspect-[3/3.4] w-full object-cover object-top"
              loading="eager"
            />
            <div className="flex items-center justify-between gap-2 border-t border-[color:var(--border-subtle)] bg-white px-4 py-3">
              <div>
                <div className="font-heading text-lg font-semibold text-[var(--brand-navy)]">Ms. A Vidal</div>
                <div className="text-xs font-medium text-[var(--brand-burgundy)]">{t.hero.cardRole}</div>
              </div>
              <img src="/assets/nina-mark.png" alt="" className="h-9 w-9 object-contain" />
            </div>
          </div>

          {/* Map badge */}
          <div className="absolute -right-3 top-6 hidden rounded-xl border border-[color:var(--border-subtle)] bg-white px-3 py-2 shadow-[var(--shadow-md)] sm:flex sm:items-center sm:gap-2">
            <MapPin className="h-4 w-4 text-[var(--brand-burgundy)]" />
            <span className="text-xs font-semibold text-[var(--brand-charcoal)]">Philadelphia, PA</span>
          </div>
        </motion.div>
      </div>

      <div className="border-t border-[color:var(--border-subtle)] bg-white/60">
        <div className="mx-auto max-w-6xl px-4 py-3 text-center text-xs font-medium tracking-wide text-[var(--muted-ink)] sm:px-6">
          {t.hero.serving}
        </div>
      </div>
    </section>
  );
};

export default Hero;
